import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatTree, MatTreeFlatDataSource, MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { BehaviorSubject, combineLatest, debounceTime, filter, firstValueFrom, map, Observable, startWith, Subject, switchMap, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ProjectsService } from '../../services/projects.service';
import { EffortsService } from '../../services/efforts.service';
import { GroupsService } from '../../services/groups.service';
import { Project, ProjectMembership } from '../../models/project';
import { HighlightPipe } from '../../pipes/highlight.pipe';
import { Group } from '../../models/group';
import { SelectionModel } from '@angular/cdk/collections';
import { NestedTreeControl } from '@angular/cdk/tree';
import { Effort } from '../../models/effort';
import { RouterModule } from '@angular/router';
import { EditListComponent } from '../edit-list/edit-list.component';
import { UsersService } from '../../services/user.service';
import { ConfirmComponent } from '../confirm/confirm.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

type TreeNode = Group | Effort | Project;
@Component({
  selector: 'app-batch-add-remove-from-users',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    DragDropModule,
    MatTabsModule,
    HighlightPipe,
    MatInputModule,
    RouterModule,
    // MatGridListModule,
    MatTreeModule,
    MatIconModule],
  templateUrl: './batch-add-remove-from-users.component.html',
  styleUrl: './batch-add-remove-from-users.component.scss'
})
export class BatchAddRemoveFromUsersComponent {
  @ViewChild('tree') tree!: MatTree<TreeNode>;
  http = inject(HttpClient);
  projectsService = inject(ProjectsService);
  groupsService = inject(GroupsService);
  data = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef);
  dialog = inject(MatDialog);
  userService = inject(UsersService);
  filter = new FormControl('');
  // membFilter = new FormControl('');
  projects: Project[] = [];
  depts: Group[] = [];
  searchTerms: string[] = [];
  // membSearchTerms: string[] = [];
  //dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  dataSource = new MatTreeNestedDataSource<TreeNode>();
  // membDataSource = new MatTreeNestedDataSource<TreeNode>();
  nodeMatcher = (a: TreeNode, b: TreeNode) => (('efforts' in a && 'efforts' in b) || ('projects' in a && 'projects' in b) || (!('projects' in a || 'efforts' in a || 'projects' in b || 'efforts' in b))) && a.id == b.id;
  selection = new SelectionModel<TreeNode>(true /* multiple */, undefined, undefined, this.nodeMatcher);
  userSelection = new SelectionModel<TreeNode>(true /* multiple */, undefined, undefined, this.nodeMatcher);
  userDeselection = new SelectionModel<TreeNode>(true /* multiple */, undefined, undefined, this.nodeMatcher);
  resetSource = new Subject<boolean>();
  resetObs = this.resetSource.asObservable().pipe(startWith(''));
  loaded = false;
  childrenAccessor = (n: TreeNode) => 'efforts' in n ? <TreeNode[]>n.efforts : ('projects' in n ? <TreeNode[]>n.projects : <TreeNode[]>[]);
  projectHash: { [id: number]: boolean } = {};
  hasSaveError = false;

  constructor() {
    // this.filter.valueChanges.pipe(
    //   startWith(''),
    //   debounceTime(200),
    //   filter(v => v != null),
    //   tap(_ => this.availableLoaded = false),
    //   switchMap<string, Observable<[string, { groups: Group[] }]>>(value => this.groupsService.filterGroups([ { chip: 'hierarchy', value } ]).pipe(map(r => [value, r])) )
    // ).subscribe((r) => {
    //   this.availableLoaded = true;
    //   this.searchTerms = r[0].split(' ');
    //   this.dataSource.data = <TreeNode[]>r[1].groups; //mapToNodes(r[1].groups);
    // })
    const filterObs = this.filter.valueChanges.pipe(
      startWith(''),
      filter(v => v != null),
      switchMap<string, Observable<[string, { groups: Group[] }]>>(value => this.groupsService.filterGroups([{ chip: 'hierarchy', value }], 1000, 0).pipe(map(r => [value, r]))));
    //const groupsObs = this.groupsService.filterGroups([]).pipe(tap(_ => { this.loaded = true }));
    // const filterObs = this.filter.valueChanges.pipe(filter(s => s != null));
    const projObs = this.projectsService.filterProjects([{ chip: 'member.hawkid', value: this.data.user.hawkId }], 10000, 0, undefined, undefined, false);
    this.resetObs.subscribe(p => console.log(p));
    combineLatest([filterObs, projObs, this.resetObs])
      .pipe(tap(_ => this.loaded = false))
      .subscribe(res => {
        console.log("refreshing data", res);
        this.searchTerms = res[0][0].split(' ');
        const refreshedData = this.applyFilterToTree(<TreeNode[]>res[0][1].groups, this.searchTerms);
        this.projectHash = res[1].projects.filter(p => p.id).reduce((o, p) => { o[p.id!] = true; return o; }, <{ [id: number]: boolean }>{});
        for (let group of refreshedData) {
          const efforts = (group as Group).efforts;
          for (let effort of efforts) {
            const projects = (effort as Effort).projects;
            for (let project of projects) {
              if (this.userSelection.isSelected(project)) {
                this.selection.select(project);
                this.userSelection.select(project);
              } else if (this.userDeselection.isSelected(project)) {
                this.selection.deselect(project);
                this.userDeselection.select(project);
              } else if (project.id && this.projectHash[project.id]) {
                this.selection.select(project);                
              } else {
                this.selection.deselect(project);
              }
            }
            if (effort.projects.length) {
              this.updateParents(effort.projects[0]);
            } else {
              this.updateParents(effort);
            }
          }  
        }
        
        const orig = [...this.expandedNodes];
        this.dataSource.data = refreshedData;
        this.reapplyExpandedState(this.dataSource.data);
        this.expandedNodes = new Set(orig);
        for (let group of refreshedData) {
          this.descendantsPartiallySelected(group, true)
        for (let effort of (group as Group).efforts) {
          this.descendantsPartiallySelected(effort, true);
        }
        }
        this.loaded = true;
      });
    // this.membFilter.valueChanges.pipe(
    //   startWith(''),
    //   debounceTime(200),
    //   filter(v => v != null),
    //   switchMap<string, Observable<[string, { projects: Project[] }]>>(value => this.projectsService.filterProjects([ { chip: 'hierarchy', value } ]).pipe(map(r => [value, r])) )
    // ).subscribe(r => {
    //   this.membSearchTerms = r[0].split(' ');
    //   // this.membDataSource.data = this.invertTree(r[1].projects);
    // })
  }

  applyFilterToTree(nodes: TreeNode[], searchTerms: string[]): TreeNode[] {
    return nodes.reduce((arr, node) => {
      if ('efforts' in node) {
        const filteredEfforts = this.applyFilterToTree(node.efforts, searchTerms) as Effort[];
        if (filteredEfforts.length || searchTerms.every(st => node.name.toLowerCase().includes(st))) arr.push({ ...node, efforts: filteredEfforts });
      } else if ('projects' in node) {
        const filteredProjects = this.applyFilterToTree(node.projects, searchTerms) as Project[];
        if (filteredProjects.length || searchTerms.every(st => node.title.toLowerCase().includes(st))) arr.push({ ...node, projects: filteredProjects });
      } else {
        if (searchTerms.every(st => node.title.toLowerCase().includes(st))) arr.push(node);
      }
      return arr;
    }, <TreeNode[]>[]);
  }

  isModified(node: TreeNode): boolean {
    if ('efforts' in node) {
      return node.efforts.some(e => this.isModified(e));
    } else if ('projects' in node) {
      return node.projects.some(p => this.isModified(p));
    }
    return !!node.id && !!this.projectHash[node.id] !== this.selection.isSelected(node);
  }

  getMembershipStatus(node: TreeNode): string {
    if (!node.id) return 'indeterminate';
    if (!this.projectHash[node.id] && this.selection.isSelected(node)) return 'added';
    if (!!this.projectHash[node.id] && !this.selection.isSelected(node)) return 'removed';
    return 'unchanged';
  }

  getParentOfMembershipStatuses(node: TreeNode): string[] {
    let statuses: string[];
    if ('efforts' in node) {
      statuses = node.efforts.map(e => this.getParentOfMembershipStatuses(e)).flat(1);
    } else if ('projects' in node) {
      statuses = node.projects.map(p => this.getParentOfMembershipStatuses(p)).flat(1);
    } else {
      return [this.getMembershipStatus(node)];
    }
    return [...new Set(statuses)];
  }

  countAdded(nodes: TreeNode[]): number {
    return nodes.reduce((sum, node) => sum + ('efforts' in node ? this.countAdded(node.efforts) : ('projects' in node ? this.countAdded(node.projects) : (this.selection.isSelected(node) && node.id && !this.projectHash[node.id] ? 1 : 0))), 0);
  }
  countRemoved(nodes: TreeNode[]): number {
    return nodes.reduce((sum, node) => sum + ('efforts' in node ? this.countRemoved(node.efforts) : ('projects' in node ? this.countRemoved(node.projects) : (!this.selection.isSelected(node) && node.id && !!this.projectHash[node.id] ? 1 : 0))), 0);
  }
  countMemberships(nodes: TreeNode[]): number {
    return nodes.reduce((sum, node) => sum + ('efforts' in node ? this.countMemberships(node.efforts) : ('projects' in node ? this.countMemberships(node.projects) : (node.id && !!this.projectHash[node.id] ? 1 : 0))), 0);
  }
  viewAdds() {
    const list = this.getAdds();
    this.dialog.open(EditListComponent, { data: {
      list,
      title: 'Project memberships to add',
      description: this.countAdded(this.dataSource.data) > 0 ? `Please review and uncheck any memberships you don't want to add to '${this.data.user.displayName}'.` : "No projects are selected to add for membership.",
      defaultSelected: true,
      getIdFn: (node: TreeNode) => node.id,
      getItemNameFn: (node: Project) => node.title } })
      .afterClosed()
      .subscribe(o => {
        if (!o.inverse) return;
        for (let node of o.inverse) {
          this.selection.deselect(node as Project);
        }
      });
  }
  flattenFiltered(nodes: TreeNode[], inclusionSelector: ((node: TreeNode) => boolean)): TreeNode[] {
    const adds = nodes.reduce((list: TreeNode[], node) => [
      ...list,
      ...('efforts' in node
        ? this.flattenFiltered(node.efforts, inclusionSelector)
        : ('projects' in node
           ? this.flattenFiltered(node.projects, inclusionSelector)
           : (inclusionSelector(node) ? [node] : []))) ],
    []);
    return adds;
  }
  getRemoves() { return this.flattenFiltered(this.dataSource.data, (node: TreeNode) => !this.selection.isSelected(node) && !!node.id && !!this.projectHash[node.id]); }
  getAdds() { return this.flattenFiltered(this.dataSource.data, (node: TreeNode) => this.selection.isSelected(node) && !!node.id && !this.projectHash[node.id]); }
  viewRemoves() {
    const list = this.getRemoves();
    this.dialog.open(EditListComponent, { data: {
      title: 'Project memberships to remove',
      description: this.countRemoved(this.dataSource.data) > 0 ? `Please review and uncheck any memberships you don't want to remove from '${this.data.user.displayName}'.` : "No projects are selected for membership removal.",
      list,
      defaultSelected: true,
      getIdFn: (node: TreeNode) => node.id,
      getItemNameFn: (node: Project) => node.title } })
      .afterClosed()
      .subscribe(o => {
        if (!o.inverse) return;
        for (let node of o.inverse) {
          this.selection.select(node as Project);
        }
      });
  }

  reapplyExpandedState(nodes: TreeNode[]) {
    nodes.forEach(node => {
      if (this.expandedNodes.has(this.id(node))) {
        // e.g., if you’re manually managing expansion logic, mark it open
        // console.log("EXPANDING")
        this.tree.expand(node);
      }
      if ('efforts' in node) {
        this.reapplyExpandedState(node.efforts);
      } else if ('projects' in node) {
        this.reapplyExpandedState(node.projects);
      }
    });

  }

  // mapDeptsToNodes(groups: Group[]) {
  //   return groups.map(g => <Node>{ name: g.name, })
  // }

  reset() {
    this.userSelection.clear();
    this.userDeselection.clear();
    this.resetSource.next(true);
  }
  cancel() { this.dialogRef.close(); }
  async save() {
    const userId = this.data.user.id;
    const adds = this.getAdds().map(a => <ProjectMembership>{ userId, projectId: a.id });
    const removes = this.getRemoves().map(r => <ProjectMembership>{ userId, projectId: r.id});
    try {
      const message = `Are you sure you want to save these membership changes?`;
      this.dialog.open(ConfirmComponent, { width: '400px', data: { title: "Are you sure?", message } })
        .afterClosed().subscribe(async result => {
          if (result) {
            await firstValueFrom(this.userService.bulkAddRemoveProjects(userId, adds, removes));
            this.dialogRef.close();
          }
        });
    } catch (err) {
      this.hasSaveError = true;
    }
  }

  hasChild = (_: number, node: TreeNode) => 'projects' in node || 'efforts' in node;

  summary(node: TreeNode) {
    if ('efforts' in node) {
      return `${node.efforts.length} efforts, ${node.efforts.reduce((s,e) => s + e.projects.length, 0)} projects`;
    } else if ('projects' in node) {
      return `${node.projects.length} projects`;
    }
    return "https://xkcd.com/2200/";
  }

  expandedNodes = new Set<string>();
  id(node: TreeNode) { return ('efforts' in node ? 'group' : ('projects' in node) ? 'effort' : 'project') + node.id; }
  isExpanded(node: TreeNode): boolean {
    return this.expandedNodes.has(this.id(node));
  }

  toggleNode(node: TreeNode): void {
    if (this.expandedNodes.has(this.id(node))) {
      this.expandedNodes.delete(this.id(node));
    } else {
      this.expandedNodes.add(this.id(node));
    }
    console.log(this.expandedNodes)
  }

  private setUserSelection(node: TreeNode) {
    if (this.selection.isSelected(node)) {
      this.userSelection.select(node);
    } else {
      this.userDeselection.select(node);
    }
  }

  toggleSelection(node: TreeNode): void {
    this.selection.toggle(node);
    this.setUserSelection(node);
    if (this.childrenAccessor(node)) {
      this.toggleDescendants(node, this.selection.isSelected(node));
    }
    this.updateParents(node);
    this.updateSummary();
  }

  updateSummary() {
    // const added = 
  }

  /** Select/deselect all descendants */
  private toggleDescendants(node: TreeNode, selected: boolean): void {
    this.childrenAccessor(node)?.forEach(child => {
      this.setUserSelection(node);
      if (selected) {
        this.selection.select(child);
      } else {
        this.selection.deselect(child);
      }
      if (this.childrenAccessor(child)) {
        this.toggleDescendants(child, selected);
      }
    });
  }

  /** Bubble state changes up to parents */
  private updateParents(node: TreeNode): void {
    const parent = this.getParent(node);
    if (!parent) return;

    const allSelected = this.childrenAccessor(parent)!.every(c => this.selection.isSelected(c));
    const noneSelected = this.childrenAccessor(parent)!.every(c => !this.selection.isSelected(c));

    if (allSelected) {
      this.selection.select(parent);
    } else {
      this.selection.deselect(parent);
    }

    this.updateParents(parent);
  }

  indexOf(node: TreeNode) {
    const i = this.dataSource.data.indexOf(node);
    return i > 0 ? i : 0;
  }

  /** Whether descendants are partially selected */
  descendantsPartiallySelected(node: TreeNode, debug = false): boolean {
    if (!this.childrenAccessor(node)) return false;
    const someSelected = this.childrenAccessor(node).some(c => this.selection.isSelected(c) || this.descendantsPartiallySelected(c));
    if (debug)
      console.log(node.key, someSelected && !this.selection.isSelected(node));
    return someSelected && !this.selection.isSelected(node);
  }

  /** Helper to find a parent (simple DFS walk since NestedTreeControl doesn’t store parents) */
  private getParent(node: TreeNode, current: TreeNode | null = null): TreeNode | null {
    const stack: TreeNode[] = [...this.dataSource.data];
    while (stack.length) {
      const n = stack.pop()!;
      if (this.childrenAccessor(n) && this.childrenAccessor(n).includes(node)) {
        return n;
      }
      if (this.childrenAccessor(n)) stack.push(...this.childrenAccessor(n));
    }
    return null;
  }
}
